import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiSearch } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import { BiEditAlt } from "react-icons/bi";
import { FaRegTrashCan, FaGripLines, FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import LoadingSpinner from "../../../../../libs/LoadingSpinner";
import pdf from "../../../../../Assets/Images/icons/pdf.png";
import Pagination from "../../../../../libs/Pagination";
import { AppContext } from "../../../../../context/appContext";
import Swal from 'sweetalert2';
import {
  getAllContent,
  addContent,
  updateContent,
  deleteContent,
  contentSequence,
  swapContentWithPage
} from "../../../../../libs/api/contentApi";

const Content = ({ currentCategoryId = null }) => {
  const { categoriesDropdown, fetchCategoriesDropdown, t, formatNum, isRTL } = useContext(AppContext);

  const [contentData, setContentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [modalMode, setModalMode] = useState("add");
  const [selectedContentId, setSelectedContentId] = useState(null);

  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [contentLink, setContentLink] = useState("");
  const [direction, setDirection] = useState("ltr");
  const [isHidden, setIsHidden] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);

  const [errors, setErrors] = useState({
    title: "",
    categoryId: "",
    contentLink: "",
    thumbnail: ""
  });

  const contentSwipeOptions = [
    { label: t('forms.ltr'), value: "ltr" },
    { label: t('forms.rtl'), value: "rtl" },
  ];

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch content when page, search, limit, or currentCategoryId changes
  useEffect(() => {
    fetchContent(true);
  }, [currentPage, debouncedSearch, limit, currentCategoryId]);

  // Fetch categories dropdown on mount
  useEffect(() => {
    fetchCategoriesDropdown();
  }, []);

  const fetchContent = async (isLoading) => {
    if (isLoading) setLoading(true);
    try {
      const categoryIdFilter = currentCategoryId === null ? 'null' : currentCategoryId;
      const response = await getAllContent(currentPage, limit, debouncedSearch, categoryIdFilter);
      if (response.status === 200 || response.status === 201) {
        setContentData(response.data.data);
        setTotalPages(response.data.totalPages);
        setTotalRecords(response.data.totalRecords);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('content.failedToFetchContent'));
    } finally {
      if (isLoading) setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    const newErrors = {};
    if (!title || !title.trim()) {
      newErrors.title = t('validation.required');
    }
    if (!categoryId) {
      newErrors.categoryId = t('validation.required');
    }
    if (!contentLink?.trim() && !attachedFile) {
      newErrors.contentLink = t('content.eitherLinkOrFile');
    }
    if (!thumbnail) {
      newErrors.thumbnail = t('content.thumbnailRequired');
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const formData = new FormData();
    formData.append("title", title.trim());
    formData.append("categoryId", categoryId);
    if (contentLink?.trim()) {
      formData.append("link", contentLink.trim());
    }
    formData.append("direction", direction);
    formData.append("isHidden", isHidden ? "true" : "false");
    if (attachedFile && attachedFile instanceof File) {
      formData.append("image", attachedFile);
    }
    if (thumbnail && thumbnail instanceof File) {
      formData.append("thumbnail", thumbnail);
    }

    setAddLoading(true);
    try {
      const response = await addContent(formData);
      if (response.status === 200 || response.status === 201) {
        fetchContent(false);
        document.getElementById("content_modal").close();
        toast.success(response?.data?.message || t('content.contentAddedSuccess'));
        clearForm();
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setAddLoading(false);
    }
  };

  const handleUpdate = async () => {
    // Validation
    const newErrors = {};
    if (!title || !title.trim()) {
      newErrors.title = t('validation.required');
    }
    if (!categoryId) {
      newErrors.categoryId = t('validation.required');
    }
    if (!contentLink?.trim() && !attachedFile) {
      newErrors.contentLink = t('content.eitherLinkOrFile');
    }
    if (!thumbnail) {
      newErrors.thumbnail = t('content.thumbnailRequired');
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const formData = new FormData();
    formData.append("title", title.trim());
    formData.append("categoryId", categoryId);
    if (contentLink?.trim()) {
      formData.append("link", contentLink.trim());
    }
    formData.append("direction", direction);
    formData.append("isHidden", isHidden ? "true" : "false");
    if (attachedFile && attachedFile instanceof File) {
      formData.append("image", attachedFile);
    }
    if (thumbnail && thumbnail instanceof File) {
      formData.append("thumbnail", thumbnail);
    }

    setAddLoading(true);
    try {
      const response = await updateContent(selectedContentId, formData);
      if (response.status === 200 || response.status === 201) {
        fetchContent(false);
        document.getElementById("content_modal").close();
        toast.success(response?.data?.message || t('content.contentUpdatedSuccess'));
        clearForm();
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setAddLoading(false);
    }
  };

  const openModal = (mode, content = null) => {
    // Refresh categories dropdown to get latest categories
    fetchCategoriesDropdown();

    setModalMode(mode);
    setSelectedContentId(null);
    setPdfFile(null);
    setThumbnailFile(null);

    if (mode === "edit" && content) {
      setSelectedContentId(content._id);
      setTitle(content.title || "");
      setCategoryId(content.categoryId?._id || "");
      setContentLink(content.link || "");
      setDirection(content.direction || "ltr");
      setIsHidden(content.isHidden || false);

      // Set existing files for display
      if (content.image) {
        setPdfFile({ name: content.image.split("/").pop(), url: content.image });
        setAttachedFile(content.image);
      }
      if (content.thumbnail) {
        setThumbnailFile({ name: content.thumbnail.split("/").pop(), url: content.thumbnail });
        setThumbnail(content.thumbnail);
      }

      setErrors({});
    } else {
      clearForm();
    }

    document.getElementById("content_modal").showModal();
  };

  const clearForm = () => {
    setTitle("");
    setCategoryId("");
    setContentLink("");
    setDirection("ltr");
    setIsHidden(false);
    setAttachedFile(null);
    setThumbnail(null);
    setPdfFile(null);
    setThumbnailFile(null);
    setErrors({ title: "", categoryId: "", contentLink: "", thumbnail: "" });
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: t('messages.deleteConfirm'),
      text: t('messages.deleteWarning'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('common.yes'),
      cancelButtonText: t('common.cancel')
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await deleteContent(id);
      if (response.status === 200 || response.status === 201) {
        fetchContent(false);
        toast.success(response?.data?.message || t('content.contentDeletedSuccess'));
      } else {
        toast.error(response?.data?.message || t('messages.somethingWentWrong'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const isValidFileType = fileName.endsWith(".pdf") || fileName.endsWith(".mp3");
      if (isValidFileType) {
        setPdfFile({ name: file.name, file: file });
        setAttachedFile(file);
        // Clear error
        setErrors((prevErrors) => ({ ...prevErrors, contentLink: "" }));
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          contentLink: t('content.invalidFileTypeContent'),
        }));
      }
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === "image/png" || file.type === "image/jpeg" || file.type === "image/jpg")) {
      setThumbnailFile({ name: file.name, file: file });
      setThumbnail(file);
      // Clear error
      setErrors((prevErrors) => ({ ...prevErrors, thumbnail: "" }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, thumbnail: t('content.invalidThumbnailType') }));
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Drag and Drop for reordering
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedItem(contentData[index]);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.parentNode);

    // Create a custom drag image
    const draggedElement = e.target;
    const rect = draggedElement.getBoundingClientRect();
    const dragImage = draggedElement.cloneNode(true);

    dragImage.style.width = rect.width + "px";
    dragImage.style.height = rect.height + "px";
    dragImage.style.opacity = "0.8";
    dragImage.style.transform = "rotate(5deg)";
    dragImage.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
    dragImage.style.backgroundColor = "white";
    dragImage.style.border = "2px solid #0D6EFD";
    dragImage.style.borderRadius = "8px";
    dragImage.style.zIndex = "9999";

    draggedElement.style.opacity = "0.3";
    e.dataTransfer.setDragImage(dragImage, rect.width / 2, rect.height / 2);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(contentData[index]);
  };

  const handleDrop = async (e, index) => {
    e.preventDefault();
    const draggedOverItem = contentData[index];

    if (draggedItem === draggedOverItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Swap sequences between the two items
    const draggedSequence = draggedItem.sequence;
    const targetSequence = draggedOverItem.sequence;

    const updatedContent = contentData.map((content) => {
      if (content._id === draggedItem._id) {
        return { ...content, sequence: targetSequence };
      }
      if (content._id === draggedOverItem._id) {
        return { ...content, sequence: draggedSequence };
      }
      return content;
    });

    // Sort by sequence to reflect the swap
    updatedContent.sort((a, b) => a.sequence - b.sequence);

    setContentData(updatedContent);
    setDraggedItem(null);
    setDragOverItem(null);

    try {
      // Update only the two swapped items
      await Promise.all([
        handleSequence(draggedItem._id, targetSequence),
        handleSequence(draggedOverItem._id, draggedSequence)
      ]);
    } catch (error) {
      toast.error(error?.response?.data?.message || t('content.failedToUpdateSequence'));
      // Revert if update fails
      fetchContent(false);
    }
  };

  const handleDragEnd = (e) => {
    if (e.target.style) {
      e.target.style.opacity = "1";
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleSequence = async (id, sequence) => {
    try {
      const payload = {
        id: id,
        sequence: sequence,
      };
      const response = await contentSequence(payload);
      if (response.status === 200 || response.status === 201) {
        fetchContent(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('content.failedToUpdateSequence'));
    }
  };

  const handleSwapWithPage = async (contentId, direction) => {
    try {
      const payload = {
        id: contentId,
        direction: direction,
        currentPage: currentPage,
        limit: limit
      };
      const response = await swapContentWithPage(payload);
      if (response.status === 200) {
        toast.success(response.data.message || t('messages.contentMovedSuccessfully'));
        fetchContent(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">{t('content.title')}</h2>

      <div className="bg-white rounded-3xl shadow">
        {/* Search and Add Content */}
        <div className="flex justify-between items-center p-6 pt-7">
          <div className="relative w-1/3">
            <span className={`absolute inset-y-0 ${isRTL ? 'right-3' : 'left-3'} flex items-center text-black`}>
              <FiSearch size={22} />
            </span>
            <input
              type="text"
              placeholder={t('forms.searchPlaceholder')}
              className={`bg-[#F7F7F9] rounded-xl ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2.5 w-full outline-none`}
              dir={isRTL ? 'rtl' : 'ltr'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => openModal("add")}
            className="flex items-center gap-2 bg-[#0D6EFD] text-white font-medium px-6 py-2 border-0 rounded-xl hover:bg-blue-700"
          >
            <IoMdAdd size={22} /> {t('content.addContent')}
          </button>
        </div>

        {/* Table */}
        {loading && (
          <div className="flex justify-center items-center h-full p-6">
            <LoadingSpinner />
          </div>
        )}
        {!loading && contentData.length === 0 && (
          <div className="flex justify-center items-center h-full p-6">
            <p className="text-gray-500">{t('content.noContent')}</p>
          </div>
        )}
        {!loading && contentData.length > 0 && (
          <div className="w-full">
            {/* Table Header */}
            <div className="bg-[#FAFAFA] text-sm font-[600] grid grid-cols-6 border-b border-[#F7F7F9]">
              <div className={`p-2 py-4 ${isRTL ? 'pr-8' : 'pl-8'}`}>{t('common.id')}</div>
              <div className="p-2 py-4">{t('forms.title')}</div>
              <div className="p-2 py-4">{t('forms.category')}</div>
              <div className="p-2 py-4">{t('content.contentType')}</div>
              <div className="p-2 py-4">{t('content.swipeDirection')}</div>
              <div className="p-2 py-4">{t('common.actions')}</div>
            </div>

            {/* Draggable Content */}
            <div>
              {contentData.map((content, index) => (
                <div
                  key={content._id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`grid grid-cols-6 hover:bg-gray-50 border-b border-[#efeff1] text-sm font-[500] select-none ${
                    draggedItem?._id === content._id ? "opacity-50 bg-gray-100 shadow-lg" : ""
                  } ${dragOverItem?._id === content._id ? "border-t-2 border-blue-500 bg-blue-50" : ""}`}
                  style={{
                    cursor: "move",
                    transition: "all 0.3s ease",
                    userSelect: "none",
                  }}
                >
                  <div className={`p-2 py-6 ${isRTL ? 'pr-8' : 'pl-8'} flex items-center gap-2`}>
                    <FaGripLines className="text-gray-400" />
                    {formatNum((currentPage - 1) * limit + index + 1)}
                  </div>
                  <div className="p-2 py-6 flex items-center gap-2">
                    {content?.title || "-"}
                    {content?.isHidden && (
                      <span className="badge badge-sm bg-gray-200 text-gray-600 border-0">{t('forms.hidden')}</span>
                    )}
                  </div>
                  <div className="p-2 py-6">{content?.categoryId?.title || "-"}</div>
                  <div className="p-2 py-6">
                    {content?.type === "pdf" ? t('forms.pdf') : content?.type === "mp3" ? t('forms.audio') : t('forms.video') || "-"}
                  </div>
                  <div className="p-2 py-6">
                    {content?.type === "pdf" ? (content?.direction === "rtl" ? t('forms.rtl') : t('forms.ltr')) : "-"}
                  </div>
                  <div className="p-2 py-6">
                    <div className="flex gap-3 items-center">
                      {index === 0 && currentPage > 1 && (
                        <button
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwapWithPage(content._id, 'previous');
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                          title={t('common.moveToPreviousPage')}
                        >
                          <FaArrowLeft size={18} />
                        </button>
                      )}
                      {index === limit - 1 && currentPage < totalPages && (
                        <button
                          draggable={false}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwapWithPage(content._id, 'next');
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                          title={t('common.moveToNextPage')}
                        >
                          <FaArrowRight size={18} />
                        </button>
                      )}
                      <BiEditAlt
                        size={24}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => openModal("edit", content)}
                      />
                      <FaRegTrashCan
                        size={20}
                        className="cursor-pointer hover:opacity-50"
                        onClick={() => handleDelete(content._id)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {!loading && contentData.length > 0 && (
          <div className="flex justify-between items-center p-6 border-t border-[#F7F7F9] text-sm text-gray-600">
            <span>
              {t('pagination.showing')} {formatNum((currentPage - 1) * limit + 1)} {t('pagination.to')}{" "}
              {formatNum(Math.min(currentPage * limit, totalRecords))} {t('pagination.of')}{" "}
              {formatNum(totalRecords)} {t('pagination.entries')}
            </span>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>
        )}
      </div>

      {/* Modal */}
      <dialog id="content_modal" className="modal shadow-[0px_1px_3px_0px_#0A0D121A]">
        <div className="modal-box min-w-[800px] rounded-2xl p-0 max-h-[700px]">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-semibold text-lg">
                {modalMode === "add" ? t('content.addContent') : t('content.editContent')}
              </h3>
              <form method="dialog">
                <button className="text-xl hover:text-red-500">
                  <IoClose />
                </button>
              </form>
            </div>

            {/* Title */}
            <label className="block text-sm font-medium mb-2">{t('forms.title')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (e.target.value.trim()) {
                  setErrors((prev) => ({ ...prev, title: "" }));
                }
              }}
              placeholder={t('forms.enterTitle')}
              className="w-full mb-6 border rounded-xl p-3 border-[#808080]"
              dir={isRTL ? 'rtl' : 'ltr'}
            />
            {errors.title && <p className="text-red-500 absolute -mt-[22px] text-xs">{errors.title}</p>}

            {/* Select Category */}
            <label className="block text-sm font-medium mb-2">{t('forms.selectCategory')}</label>
            <select
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
                if (e.target.value) {
                  setErrors((prev) => ({ ...prev, categoryId: "" }));
                }
              }}
              className="w-full mb-6 border rounded-xl p-3 border-[#808080]"
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <option value="">{t('forms.selectCategory')}</option>
              {categoriesDropdown?.data?.map((category) => (
                <option key={category._id} value={category._id}>
                  {category.displayTitle || category.title}
                </option>
              ))}
            </select>
            {errors.categoryId && <p className="text-red-500 absolute -mt-[22px] text-xs">{errors.categoryId}</p>}

            {/* Content Link */}
            <label className="block text-sm font-medium mb-2">{t('content.addLink')}</label>
            <input
              type="text"
              value={contentLink}
              onChange={(e) => {
                setContentLink(e.target.value);
                if (e.target.value.trim() || attachedFile) {
                  setErrors((prev) => ({ ...prev, contentLink: "" }));
                }
              }}
              placeholder={t('content.enterContentLink')}
              className="w-full mb-6 border rounded-xl p-3 border-[#808080]"
              dir={isRTL ? 'rtl' : 'ltr'}
            />
            {errors.contentLink && <p className="text-red-500 absolute -mt-[22px] text-xs">{errors.contentLink}</p>}

            {/* Swipe Direction */}
            {(pdfFile?.file?.type === "application/pdf" ||
              pdfFile?.url ||
              (attachedFile && (typeof attachedFile === 'object' ? attachedFile.type === "application/pdf" : attachedFile.includes('.pdf')))) && (
              <>
                <label className="block text-sm font-medium mb-2">{t('content.swipeDirection')}</label>
                <select
                  value={direction}
                  onChange={(e) => setDirection(e.target.value)}
                  className="w-full mb-6 border rounded-xl p-3 border-[#808080]"
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  {contentSwipeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </>
            )}

            {/* Attach File */}
            <div className="flex flex-col-reverse items-baseline gap-5 mt-2">
              <label className="cursor-pointer flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <IoMdAdd size={22} />
                <span className="text-sm font-medium">{t('content.attachFile')}</span>
                <input type="file" accept=".pdf,.mp3" className="hidden" onChange={handleFileChange} />
              </label>

              {pdfFile && (
                <div className="text-sm flex w-full justify-between items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <div className="flex gap-2 items-center">
                    <img src={pdf} alt="pdf" />
                    <div>
                      <span className="font-medium">{pdfFile.name}</span>
                      {pdfFile.url && <div className="text-xs text-gray-500">{t('content.existingFile')}: {pdfFile.url.split("/").pop()}</div>}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-black-500 hover:text-red-700"
                    onClick={() => {
                      setPdfFile(null);
                      setAttachedFile(null);
                      if (!contentLink?.trim()) {
                        setErrors((prev) => ({ ...prev, contentLink: t('content.eitherLinkOrFile') }));
                      }
                    }}
                  >
                    <IoClose size={18} />
                  </button>
                </div>
              )}
            </div>

            {/* Thumbnail */}
            <div className="flex flex-col-reverse items-baseline gap-5 mt-2">
              <label className="cursor-pointer flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <IoMdAdd size={22} />
                <span className="text-sm font-medium">{t('content.attachThumbnail')}</span>
                <input type="file" accept=".png,.jpg,.jpeg" className="hidden" onChange={handleThumbnailChange} />
              </label>

              {thumbnailFile && (
                <div className="text-sm flex w-full justify-between items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <div className="flex gap-2 items-center">
                    {thumbnailFile.url ? (
                      <img src={thumbnailFile.url} alt="thumbnail" className="w-8 h-8 object-cover rounded" />
                    ) : (
                      <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center">
                        <span className="text-xs">IMG</span>
                      </div>
                    )}
                    <div>
                      <span className="font-medium">{thumbnailFile.name}</span>
                      {thumbnailFile.url && <div className="text-xs text-gray-500">{t('content.existingThumbnail')}</div>}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-black-500 hover:text-red-700"
                    onClick={() => {
                      setThumbnailFile(null);
                      setThumbnail(null);
                      setErrors((prev) => ({ ...prev, thumbnail: t('content.thumbnailRequired') }));
                    }}
                  >
                    <IoClose size={18} />
                  </button>
                </div>
              )}
            </div>
            {errors.thumbnail && <p className="text-red-500 absolute mt-[2px] text-xs">{errors.thumbnail}</p>}

            {/* Hidden */}
            <div className="flex items-center gap-2 mt-8">
              <input
                type="checkbox"
                id="content_isHidden"
                checked={isHidden}
                onChange={(e) => setIsHidden(e.target.checked)}
                className="checkbox checkbox-sm"
              />
              <label htmlFor="content_isHidden" className="text-sm font-medium cursor-pointer">
                {t('forms.hidden')}
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="modal-action bg-[#fafafa] mt-6 p-6 py-4 justify-end flex">
            <button
              type="button"
              disabled={addLoading || loading}
              onClick={modalMode === "add" ? handleSubmit : handleUpdate}
              className="btn bg-[#0D6EFD] rounded-xl p-2 px-5 text-white font-medium hover:bg-blue-700"
            >
              {modalMode === "add" ? (addLoading ? <LoadingSpinner /> : t('common.add')) : modalMode === "edit" ? (addLoading ? <LoadingSpinner /> : t('common.update')) : t('common.cancel')}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default Content;
